import { type MutableRefObject, useEffect, useMemo, useRef } from 'react';
import { zfd } from '@dinewise/utils';
import { withZod } from '@michaelgmcd/rvf';
import * as z from 'zod';

export type PaymentFormTokenResponse = {
  status: number;
  data: {
    id: string;
    created_at: string;
    currency: string;
    expires_at: string;
    fingerprint: string;
    instrument_type: string;
    updated_at: string;
    _embedded?: { errors: Array<{ code: string; field: string; message: string }> };
  };
};
export type PaymentFormSubmitHandler = (
  err: unknown,
  res: PaymentFormTokenResponse | undefined,
) => void;

export type PaymentForm = {
  state: Record<string, { errorMessages: string[]; isDirty: boolean }>;
  submit: (
    env: 'sandbox' | 'live',
    applicationId: string,
    submitHandler: PaymentFormSubmitHandler,
  ) => void;
};

type Field =
  | 'name'
  | 'security_code'
  | 'address_line1'
  | 'address_line2'
  | 'address_city'
  | 'address_state'
  | 'address_region'
  | 'address_country'
  | 'address_postal_code';

const defaultRequiredFields: Field[] = [
  'name',
  'address_line1',
  'address_city',
  'address_region',
  'address_country',
  'address_postal_code',
];

export type PaymentFormFieldLabels = {
  fullName: string;
  routingNumber: string;
  expirationPlaceholder: string;
};

const defaultPaymentFormFieldLabels: PaymentFormFieldLabels = {
  fullName: 'Full Name',
  routingNumber: 'Routing Number',
  expirationPlaceholder: 'MM/YY',
};

export type PaymentFormModalProps = {
  showAddress?: boolean;
  hideFields?: Field[];
  requiredFields?: Field[];
  className?: string;
  formRef: MutableRefObject<PaymentForm | null>;
  supportedMethods: Array<'CREDIT_CARD' | 'ACH'>;
  setFinixFormIsValid?: (val: boolean) => void;
  setFinixFormLoaded?: (val: boolean) => void;
  formLabels?: PaymentFormFieldLabels;
};

export const accountCategories = {
  personal: 'Personal',
  corporate: 'Corporate',
};

export const createPaymentInstrumentValidator = withZod(
  z.object({
    identity: zfd.text(),
    token: zfd.text(),
    accountCategory: zfd.text(z.enum(['personal', 'corporate'])),
    reservationScope: zfd.text(z.string().optional()).optional(),
  }),
);

export const completeCheckoutInstrumentCreationValidator = withZod(
  z.object({
    identity: zfd.text(),
    paymentProviderToken: zfd.text(),
    zipCode: zfd.text(),
    reservationScope: zfd.text(z.string().optional()).optional(),
  }),
);

export const createIdentityAndPaymentInstrumentValidator = withZod(
  z.object({
    userId: zfd.text(),
    token: zfd.text(),
    accountCategory: zfd.text(z.enum(['personal', 'corporate'])),
    reservationScope: zfd.text(z.string().optional()).optional(),
  }),
);

export const createCKOPaymentSessionValidator = withZod(
  z
    .object({
      reservationScope: zfd.text(z.string().optional()),
      supportAch: zfd.text(),
    })
    .transform(source => ({
      ...source,
      supportAch: source.supportAch === 'true',
    })),
);

type PaymentFormOptions = {
  onSubmit?: () => void;
  onUpdate?: (
    state: Record<string, { errorMessages: string[] }> | undefined | null,
    binInformation: unknown,
    formHasErrors: boolean,
  ) => void;
  hideFields?: Field[];
  requiredFields?: Field[];
  hideErrorMessages?: boolean;
  labels?: Record<string, string>;
  errorMessages?: Record<string, string>;
  showAddress?: boolean;
  showLabels?: boolean;
  placeholders?: Record<string, string>;
};
// Finix Payment Forms is loaded with a script tag, so we manually type it here
type FinixAuth = {
  getSessionKey: () => string;
};
declare global {
  // oxlint-disable-next-line consistent-type-definitions
  interface Window {
    Finix?: {
      TokenForm: (elementId: string, options: PaymentFormOptions) => PaymentForm;
      BankTokenForm: (elementId: string, options: PaymentFormOptions) => PaymentForm;
      CardTokenForm: (elementId: string, options: PaymentFormOptions) => PaymentForm;
      Auth: (
        env: 'sandbox' | 'live',
        merchantId: string,
        callback: (sessionKey: string) => void,
      ) => FinixAuth;
    };
  }
}
export const AddPaymentMethodForm = (props: PaymentFormModalProps) => {
  const {
    className,
    showAddress,
    hideFields,
    requiredFields: inputRequiredFields,
    formRef,
    supportedMethods,
    setFinixFormIsValid,
    setFinixFormLoaded,
    formLabels: formLabelsProp,
  } = props;
  const formLabels = formLabelsProp ?? defaultPaymentFormFieldLabels;
  const finixFormContainerRef = useRef<HTMLDivElement | null>(null);
  const finixFormElementId = 'finix-payment-form';

  const requiredFields = useMemo(() => {
    if (inputRequiredFields) {
      return inputRequiredFields;
    }

    if (hideFields) {
      return defaultRequiredFields.filter(field => !hideFields.includes(field));
    }

    return defaultRequiredFields;
  }, [hideFields, inputRequiredFields]);

  const clientSupportsCards = supportedMethods.includes('CREDIT_CARD');
  const clientSupportsACH = supportedMethods.includes('ACH');

  useEffect(() => {
    if (!finixFormContainerRef.current) {
      return;
    }

    const createForm = (() => {
      if (clientSupportsCards && clientSupportsACH) {
        return window.Finix?.TokenForm.bind(window.Finix);
      }
      if (clientSupportsCards) {
        return window.Finix?.CardTokenForm.bind(window.Finix);
      }
      if (clientSupportsACH) {
        return window.Finix?.BankTokenForm.bind(window.Finix);
      }

      return null;
    })();

    if (!createForm) {
      return;
    }

    const form = createForm(finixFormElementId, {
      showAddress,
      labels: {
        name: formLabels.fullName,
        bank_code: formLabels.routingNumber,
        institution_number: formLabels.routingNumber,
      },
      placeholders: {
        expiration_date: formLabels.expirationPlaceholder,
      },
      hideFields: hideFields ?? [],
      requiredFields,
      onUpdate: (_state, _binInformation, formHasErrors) => {
        setFinixFormIsValid?.(!formHasErrors);
      },
    });
    // finixFormRef.current = form;
    formRef.current = form;
    setFinixFormLoaded?.(true);

    return () => {
      finixFormContainerRef.current = null;
    };
  }, [
    clientSupportsACH,
    clientSupportsCards,
    formRef,
    hideFields,
    requiredFields,
    setFinixFormIsValid,
    setFinixFormLoaded,
    showAddress,
    formLabels.fullName,
    formLabels.routingNumber,
    formLabels.expirationPlaceholder,
  ]);

  return <div {...{ className }} id={finixFormElementId} ref={finixFormContainerRef} />;
};
